/**
 * MapDecorations — themed game decorations layered over the map ocean.
 *
 * Now fully interactive and styled to match the red/blue game palette:
 *   - CompassRose: clickable, animates on hover, shows bearing tooltip
 *   - TitleCartouche: clickable, links to How To Play  
 *   - ScaleBar: shows cube count scale reference
 *   - Graticule: faint lat/lon lines
 *
 * Compass and Cartouche use pointer-events: all so players can interact.
 */

import { type ReactNode, useState } from 'react';
import { useIsMobile } from '../../hooks/useIsMobile';

/** Faint latitude/longitude lines on the ocean. */
export function Graticule(): ReactNode {
  const verticals   = [100, 200, 300, 400, 500, 600, 700, 800, 900];
  const horizontals = [75, 150, 225, 300, 375, 450, 525];
  return (
    <g
      data-layer="graticule"
      stroke="#ffffff"
      strokeOpacity={0.06}
      strokeWidth={0.6}
      fill="none"
      strokeDasharray="2 6"
      pointerEvents="none"
    >
      {verticals.map((x) => (
        <line key={`v-${x}`} x1={x} y1={0} x2={x} y2={600} />
      ))}
      {horizontals.map((y) => (
        <line key={`h-${y}`} x1={0} y1={y} x2={1000} y2={y} />
      ))}
      {/* Equator bolder */}
      <line x1={0} y1={300} x2={1000} y2={300} strokeOpacity={0.12} strokeDasharray="6 8" />
    </g>
  );
}

/**
 * CompassRose — interactive, themed in red/blue game palette.
 * Click to see directional info. Pulses on hover.
 */
export function CompassRose({
  cx = 925,
  cy = 468,
  size = 28,
}: {
  readonly cx?: number;
  readonly cy?: number;
  readonly size?: number;
}): ReactNode {
  // The compass sits at SVG (cx=925) which is already off-canvas on a
  // typical mobile viewBox crop; rendering it just adds visual clutter
  // and a circle the player can't see. Bail early on mobile.
  const isMobile = useIsMobile();
  const [hovered, setHovered] = useState(false);
  const [clicked, setClicked] = useState(false);
  if (isMobile) return null;

  const longArm  = size;
  const shortArm = size * 0.52;

  const points: [number, number][] = [
    [cx,                    cy - longArm               ], // N
    [cx + shortArm * 0.45,  cy - shortArm * 0.45       ], // NE
    [cx + longArm,          cy                         ], // E
    [cx + shortArm * 0.45,  cy + shortArm * 0.45       ], // SE
    [cx,                    cy + longArm               ], // S
    [cx - shortArm * 0.45,  cy + shortArm * 0.45       ], // SW
    [cx - longArm,          cy                         ], // W
    [cx - shortArm * 0.45,  cy - shortArm * 0.45       ], // NW
  ];
  const star = `M${points[0][0]},${points[0][1]} ${points.slice(1).map(([x, y]) => `L${x},${y}`).join(' ')} Z`;

  // Mirror star (shadowed half)
  const mirror: [number, number][] = [
    [cx,                    cy - longArm               ],
    [cx - shortArm * 0.45,  cy - shortArm * 0.45       ],
    [cx - longArm,          cy                         ],
    [cx - shortArm * 0.45,  cy + shortArm * 0.45       ],
    [cx,                    cy + longArm               ],
    [cx + shortArm * 0.45,  cy + shortArm * 0.45       ],
    [cx + longArm,          cy                         ],
    [cx + shortArm * 0.45,  cy - shortArm * 0.45       ],
  ];
  const mirrorPath = `M${mirror[0][0]},${mirror[0][1]} ${mirror.slice(1).map(([x, y]) => `L${x},${y}`).join(' ')} Z`;

  // Tick marks
  const ticks: ReactNode[] = [];
  for (let deg = 0; deg < 360; deg += 30) {
    const r  = (deg * Math.PI) / 180;
    const r1 = size + 6;
    const r2 = size + (deg % 90 === 0 ? 12 : 8);
    ticks.push(
      <line
        key={`tick-${deg}`}
        x1={cx + Math.sin(r) * r1} y1={cy - Math.cos(r) * r1}
        x2={cx + Math.sin(r) * r2} y2={cy - Math.cos(r) * r2}
        stroke={deg % 90 === 0 ? '#ef4444' : '#ffffff'}
        strokeOpacity={deg % 90 === 0 ? 0.9 : 0.4}
        strokeWidth={deg % 90 === 0 ? 1.5 : 0.7}
      />,
    );
  }

  const scale = hovered ? 1.12 : 1;

  return (
    <g
      data-layer="compass-rose"
      style={{
        cursor: 'pointer',
        transform: `scale(${scale})`,
        transformOrigin: `${cx}px ${cy}px`,
        transition: 'transform 0.2s ease',
      }}
      onClick={() => setClicked((v) => !v)}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Backdrop disc — deep navy */}
      <circle
        cx={cx} cy={cy} r={size + 36}
        fill="#0a1628"
        stroke={hovered ? '#ef4444' : '#1d4ed8'}
        strokeWidth={hovered ? 2.5 : 2}
        style={{ filter: 'drop-shadow(0px 6px 18px rgba(0,0,0,0.7))' }}
      />
      {/* Outer blue ring */}
      <circle
        cx={cx} cy={cy} r={size + 20}
        fill="none"
        stroke="#1d4ed8"
        strokeOpacity={0.5}
        strokeWidth={1}
      />
      {/* Dashed ring */}
      <circle
        cx={cx} cy={cy} r={size + 28}
        fill="none"
        stroke="#ffffff"
        strokeOpacity={0.12}
        strokeWidth={0.7}
        strokeDasharray="2 4"
      />
      {ticks}

      {/* North indicator highlight */}
      <circle
        cx={cx} cy={cy - size - 20} r={7}
        fill={hovered ? '#ef4444' : '#dc2626'}
        fillOpacity={0.9}
      />
      <text
        x={cx} y={cy - size - 16}
        textAnchor="middle"
        fill="#ffffff"
        fontSize={8}
        fontWeight={800}
        fontFamily="ui-monospace, monospace"
      >N</text>

      {/* Shadow star half */}
      <path d={mirrorPath} fill="#1d4ed8" fillOpacity={0.25} stroke="none" />
      {/* Main star outline — white */}
      <path
        d={star}
        fill={hovered ? 'rgba(239,68,68,0.15)' : 'rgba(255,255,255,0.05)'}
        stroke={hovered ? '#ef4444' : '#ffffff'}
        strokeOpacity={hovered ? 0.95 : 0.75}
        strokeWidth={1.2}
        strokeLinejoin="round"
      />
      {/* Center hub */}
      <circle cx={cx} cy={cy} r={4} fill={hovered ? '#ef4444' : '#1d4ed8'} />
      <circle cx={cx} cy={cy} r={2} fill="#ffffff" fillOpacity={0.9} />

      {/* E/S/W small labels */}
      {(['E', 'S', 'W'] as const).map((label, i) => {
        const angles = [90, 180, 270];
        const angle  = (angles[i]! * Math.PI) / 180;
        const lx     = cx + Math.sin(angle) * (size + 20);
        const ly     = cy - Math.cos(angle) * (size + 20);
        return (
          <text
            key={label}
            x={lx} y={ly + 3}
            textAnchor="middle"
            fill="#ffffff"
            fillOpacity={0.55}
            fontSize={8}
            fontFamily="ui-monospace, monospace"
          >
            {label}
          </text>
        );
      })}

      {/* Tooltip on click */}
      {clicked ? (
        <g>
          <rect
            x={cx - 52} y={cy - size - 65}
            width={104} height={32}
            fill="#0a1628" stroke="#ef4444" strokeWidth={1.5} rx={6}
          />
          <text
            x={cx} y={cy - size - 55}
            textAnchor="middle" fill="#ffffff" fontSize={8}
            fontFamily="ui-monospace, monospace" letterSpacing="0.1em"
          >
            QUALITY DOMINATION
          </text>
          <text
            x={cx} y={cy - size - 43}
            textAnchor="middle" fill="#ef4444" fontSize={7}
            fontFamily="ui-monospace, monospace"
          >
            CONQUER · DEFEND · DOMINATE
          </text>
        </g>
      ) : null}

      <style>{`
        @keyframes qd-compass-spin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
      `}</style>
    </g>
  );
}

/**
 * TitleCartouche — interactive plaque. Click to toggle game info panel.
 * Styled in the game's red/blue theme.
 */
export function TitleCartouche({
  x = 22,
  y = 420,
  width: widthProp = 220,
  height: heightProp = 60,
}: {
  readonly x?: number;
  readonly y?: number;
  readonly width?: number;
  readonly height?: number;
}): ReactNode {
  const [hovered, setHovered] = useState(false);
  const [open, setOpen] = useState(false);
  // Mobile: shrink the plaque so it doesn't cover ~⅔ of the bottom-left
  // map area on a 360px-wide phone. The internal text sizes track the
  // box so the plaque stays legible at the smaller footprint.
  const isMobile = useIsMobile();
  const width = isMobile ? 180 : widthProp;
  const height = isMobile ? 50 : heightProp;
  const titleSize = isMobile ? 12 : 15;
  const subtitleSize = isMobile ? 7 : 8;
  const footerSize = isMobile ? 5 : 6;
  const cx = x + width / 2;

  // On mobile, the inline SVG info panel is too cramped to be useful and
  // the cartouche is the only entry point to in-game help (the desktop
  // StatusBar's HowToPlayButton isn't rendered on mobile). Route taps to
  // the full HowToPlayModal via its global window event instead of
  // toggling the inline panel.
  const handleClick = (): void => {
    if (isMobile) {
      window.dispatchEvent(new CustomEvent('qd:show-howto'));
      return;
    }
    setOpen((v) => !v);
  };

  return (
    <g
      data-layer="cartouche"
      style={{ cursor: 'pointer' }}
      onClick={handleClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Shadow */}
      <rect
        x={x + 4} y={y + 4} width={width} height={height}
        fill="rgba(0,0,0,0.45)" rx={10}
      />
      {/* Main plaque — deep navy */}
      <rect
        x={x} y={y} width={width} height={height}
        fill="#0a1628"
        stroke={hovered ? '#ef4444' : '#1d4ed8'}
        strokeWidth={hovered ? 2.5 : 2}
        rx={10}
        style={{ transition: 'stroke 0.2s ease' }}
      />
      {/* Red accent top bar */}
      <rect x={x + 10} y={y} width={width - 20} height={3} fill="#ef4444" rx={1.5} />

      {/* Corner dots — alternating red/blue */}
      {([[x+12,y+12],[x+width-12,y+12],[x+12,y+height-12],[x+width-12,y+height-12]] as [number,number][]).map(([dcx,dcy],i) => (
        <circle key={i} cx={dcx} cy={dcy} r={2.5}
          fill={i % 2 === 0 ? '#ef4444' : '#1d4ed8'}
          fillOpacity={0.85}
        />
      ))}

      {/* Title text — y offsets shift up slightly when the plaque is shorter */}
      <text
        x={cx} y={y + (isMobile ? 21 : 25)}
        textAnchor="middle"
        fill={hovered ? '#ef4444' : '#ffffff'}
        fontSize={titleSize}
        fontFamily="'Fraunces', Georgia, serif"
        letterSpacing="0.14em"
        fontWeight={700}
        style={{ transition: 'fill 0.2s ease' }}
      >
        QUALITY DOMINATION
      </text>
      <text
        x={cx} y={y + (isMobile ? 35 : 42)}
        textAnchor="middle"
        fill="#60a5fa"
        fillOpacity={0.8}
        fontSize={subtitleSize}
        fontFamily="ui-monospace, monospace"
        letterSpacing="0.28em"
      >
        {isMobile ? 'TAP FOR FULL RULES' : 'TAP FOR GAME INFO'}
      </text>
      <text
        x={cx} y={y + height - 6}
        textAnchor="middle"
        fill="#ffffff"
        fillOpacity={0.3}
        fontSize={footerSize}
        fontFamily="ui-monospace, monospace"
        letterSpacing="0.35em"
      >
        A WORLD OF QA · MMXXVI
      </text>

      {/* Info panel on click. Anchored above the plaque; panelY is
          clamped so the rect never starts above the SVG viewBox top
          edge (y=0) on short viewports — without the clamp, opening
          the panel on a phone in landscape would render the heading
          off-canvas. */}
      {open ? (() => {
        const panelHeight = 94;
        const panelY = Math.max(4, y - panelHeight - 6);
        return (
          <g>
            <rect
              x={x} y={panelY} width={width} height={panelHeight}
              fill="#0a1628" stroke="#1d4ed8" strokeWidth={1.5} rx={8}
            />
            <text x={cx} y={panelY + 18} textAnchor="middle"
              fill="#ef4444" fontSize={9} fontWeight={800}
              fontFamily="ui-monospace, monospace" letterSpacing="0.15em"
            >HOW TO WIN</text>
            {[
              '1. Click YOUR territory (2+ cubes)',
              '2. Click enemy territory next to it',
              '3. Answer a QA question to attack',
              '4. Conquer all territories to win!',
            ].map((line, i) => (
              <text key={i}
                x={x + 10} y={panelY + 34 + i * 14}
                fill="#e2e8f0" fontSize={8}
                fontFamily="ui-monospace, monospace"
              >
                {line}
              </text>
            ))}
            <text x={cx} y={panelY + panelHeight - 8} textAnchor="middle"
              fill="#60a5fa" fontSize={7} fontFamily="ui-monospace, monospace"
            >[tap to close]</text>
          </g>
        );
      })() : null}
    </g>
  );
}

/**
 * ScaleBar — indicates the cube count scale. Styled in game palette.
 */
export function ScaleBar({
  x = 260,
  y = 494,
  width = 140,
  segments = 4,
}: {
  readonly x?: number;
  readonly y?: number;
  readonly width?: number;
  readonly segments?: number;
}): ReactNode {
  const seg = width / segments;
  return (
    <g data-layer="scale-bar" pointerEvents="none">
      {/* Background */}
      <rect
        x={x - 12} y={y - 12}
        width={width + 24} height={28}
        fill="#0a1628"
        stroke="#1d4ed8"
        strokeWidth={1.5}
        rx={6}
        style={{ filter: 'drop-shadow(0px 4px 10px rgba(0,0,0,0.5))' }}
      />
      {/* Scale bar line */}
      <line x1={x} y1={y} x2={x + width} y2={y} stroke="#ffffff" strokeOpacity={0.6} strokeWidth={1.2} />
      {/* Tick marks */}
      {Array.from({ length: segments + 1 }).map((_, i) => (
        <line key={i}
          x1={x + i * seg} y1={y - 4}
          x2={x + i * seg} y2={y + 4}
          stroke={i === 0 || i === segments ? '#ef4444' : '#ffffff'}
          strokeOpacity={0.7}
          strokeWidth={i === 0 || i === segments ? 1.5 : 0.8}
        />
      ))}
      {/* Alternating filled bars */}
      {Array.from({ length: segments }).map((_, i) =>
        i % 2 === 0 ? null : (
          <rect key={`bar-${i}`}
            x={x + i * seg} y={y - 2}
            width={seg} height={4}
            fill="#1d4ed8" fillOpacity={0.7}
          />
        ),
      )}
      <text x={x} y={y + 14}
        fill="#ffffff" fillOpacity={0.5}
        fontSize={7} fontFamily="ui-monospace, monospace" letterSpacing="0.15em"
      >0</text>
      <text x={x + width} y={y + 14}
        textAnchor="end"
        fill="#ffffff" fillOpacity={0.5}
        fontSize={7} fontFamily="ui-monospace, monospace" letterSpacing="0.15em"
      >∞ TERRITORIES</text>
    </g>
  );
}
